import React, { useState } from 'react';
import { useBars } from '../hooks/useBars';
import type { Bar } from '../hooks/useBars';
import { BarModal } from '../components/BarModal';
import { useAuthContext } from '../contexts/AuthContext';
import { logAction, getCurrentAdmin } from '../services/logger';
import { supabase } from '../lib/supabase';
import { Pencil, Trash2, MapPin, Search } from 'lucide-react';
import toast from 'react-hot-toast';

export function BarsPage() {
  const { canDelete } = useAuthContext();
  const { bars, loading, updateBar, refetch } = useBars();
  const [editBar, setEditBar] = useState<Bar | null>(null);
  const [search, setSearch] = useState('');

  const filtered = search
    ? bars.filter(b => b.name.toLowerCase().includes(search.toLowerCase()) || b.address.toLowerCase().includes(search.toLowerCase()))
    : bars;

  const handleSave = async (id: string, data: Partial<Bar>) => {
    const admin = getCurrentAdmin();
    const bar = bars.find(b => b.id === id);
    await updateBar(id, data);
    await logAction('update_bar', 'bar', id,
      { name: bar?.name, address: bar?.address },
      data,
      `${admin?.name} a modifié le bar ${data.name}`
    );
    toast.success('Bar mis à jour');
  };

  const handleDeleteBar = async (bar: Bar) => {
    if (!canDelete) {
      toast.error('Seuls les super admins peuvent supprimer des bars');
      return;
    }
    if (!window.confirm(`Supprimer le bar "${bar.name}" et toutes ses diffusions ?`)) return;
    const admin = getCurrentAdmin();
    await supabase.from('diffusions').delete().eq('bar_id', bar.id);
    await supabase.from('bars').delete().eq('id', bar.id);
    await logAction('delete_bar', 'bar', bar.id,
      { name: bar.name, address: bar.address }, null,
      `${admin?.name} a supprimé le bar ${bar.name}`
    );
    toast.success('Bar supprimé');
    refetch();
  };

  return (
    <div>
      <div className="filters-row">
        <div className="filter-search">
          <Search size={16} />
          <input placeholder="Rechercher un bar..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{filtered.length} bars</span>
      </div>

      <div className="card">
        {loading ? (
          <div className="skeleton-list">
            {[1,2,3,4].map(i => <div key={i} className="skeleton-row" />)}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Statut</th>
                <th>Nom</th>
                <th>Adresse</th>
                <th>Coordonnées</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(bar => {
                const hasCoords = bar.latitude && bar.longitude && !(bar.latitude === 50.6292 && bar.longitude === 3.0573);
                return (
                  <tr key={bar.id}>
                    <td>
                      <span className={`conn-dot ${bar.is_online ? 'green' : 'red'}`}
                            style={{ display: 'inline-block' }}
                            title={bar.is_online ? 'En ligne' : 'Hors ligne'} />
                    </td>
                    <td style={{ fontWeight: 600 }}>{bar.name}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{bar.address}</td>
                    <td>
                      {hasCoords ? (
                        <span style={{ fontSize: '11px', color: 'var(--green)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <MapPin size={12} /> {bar.latitude!.toFixed(4)}, {bar.longitude!.toFixed(4)}
                        </span>
                      ) : (
                        <span style={{ fontSize: '11px', color: 'var(--orange)' }}>Pas de coords</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn-icon" onClick={() => setEditBar(bar)} title="Éditer">
                        <Pencil size={15} />
                      </button>
                      {canDelete && (
                        <button className="btn-icon danger" onClick={() => handleDeleteBar(bar)} title="Supprimer">
                          <Trash2 size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editBar && (
        <BarModal bar={editBar} onSave={handleSave} onClose={() => setEditBar(null)} />
      )}
    </div>
  );
}
